import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../commonInfra/components/FormikCheckbox';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { SiteAuthenticationSettingsService } from '../../../siteAuthSettings/services/SiteAuthenticationSettingsService';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../site-settings/ReactSiteSettingsForSiteAdmin';

export type SiteInviteNotificationSectionProps = SiteSettingsForSiteAdminProps<'siteInviteEmailEnabled'>;
export type SiteInviteNotificationSectionInternalProps = {
              sessionManagementService: SessionManagementService;
            } & SiteInviteNotificationSectionProps;

export function SiteInviteNotificationSectionInternal(props: SiteInviteNotificationSectionInternalProps) {
  const siteInviteEmailEnabledForServer = props.sessionManagementService.isSiteInviteEmailEnabledForServer();
  const isSiteInviteNotificationUnavailable = useIsSiteNotificationUnavailable(siteInviteEmailEnabledForServer);
  const spacer = style({
    paddingBottom: TabStyles.Spacing.MediumSpace,
    paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
    fontStyle: 'italic'
  });

  if (!siteInviteEmailEnabledForServer) {
    return null;
  }
  return <SettingsSection title={Messages.siteInviteSettingSection()} description={Messages.siteInviteSettingsDetails()}>
      <FormikCheckbox
        name={'settings.siteInviteEmailEnabled'}
        label={Messages.siteInviteSettingsEnabled()}
        testId={'site-invite-checkbox'}
        disabled={isSiteInviteNotificationUnavailable}
      />
      {
        isSiteInviteNotificationUnavailable &&
        <div className={spacer} id='site-invite-notification-unavailable-id'>
          {Messages.siteInviteSettingsDisabledWarning()}
        </div>
      }
    </SettingsSection>;
}

function useIsSiteNotificationUnavailable(siteInviteEmailEnabledForServer: boolean) {
  const [isSiteNotificationUnavailable, setisSiteNotificationUnavailable] = React.useState<boolean>(null);
  React.useEffect(
    () => {
      if (siteInviteEmailEnabledForServer && isSiteNotificationUnavailable === null) {
        const authSettings = SiteAuthenticationSettingsService.getSiteAuthenticationSettings();
        authSettings.then(authSettings => setisSiteNotificationUnavailable(!SiteAuthenticationSettingsService.isAuthenticationSSO(authSettings)));
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteInviteEmailEnabledForServer]
  );
  return isSiteNotificationUnavailable;
}

/**
 * A wrapper component which provides the dependencies to the internal component
 * @param props
 * @constructor
 */
export function SiteInviteNotificationSection(props: SiteInviteNotificationSectionProps) {
  const deps: SiteInviteNotificationSectionInternalProps = {
    sessionManagementService: getSessionManagementServiceInstance(),
    settings: props.settings,
    siteId: props.siteId
  };
  return <SiteInviteNotificationSectionInternal {...deps} />;
}

/* for registering the component in settings registry */
export const SiteInviteNotificationSpecs = componentRegistryEntry({
  name: 'SiteInviteNotificationSection',
  component: SiteInviteNotificationSection
});
